import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Recipient, Service } from 'webapp-types';
import { DeliveryType, type BillingFeature } from 'webapp/enums/enums';
import {
	NotificationFilterFrequency,
	NotificationFilterType,
	NotificationScheduleType,
} from 'webapp/enums/notifications';
import { getDeliveryTimeLevel, getRelativeTime } from 'webapp/utils/number';

import type { UserRecord } from 'webapp/models';
import Serviced from 'webapp/models-custom/serviced';

import type { ModelWithNotification } from 'webapp/services/model-manager/notifications';

export default abstract class NotificationBase extends Serviced {
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('account') declare accountService: Service.Account;

	@tracked activeFilter: string[] = [];

	abstract type: NotificationFilterType;
	abstract targetObject: ModelWithNotification;
	abstract typeColor: string;
	abstract typeIcon: IconName;

	abstract get title(): string;
	abstract get subtitleText(): string;
	abstract get isMine(): boolean; // if i am the creator -- diff logics when removing notifications
	abstract get scheduleType(): NotificationScheduleType;
	abstract get frequency(): NotificationFilterFrequency[]; // only goal currenly can have more than 1
	abstract get frequencyTooltipTextTop(): string;
	abstract get createdBy(): UserRecord | null;
	abstract get createdOn(): number | null;
	abstract get deliveryTypes(): DeliveryType[];
	abstract get emailRecipients(): (number | string)[]; // for filter: user ids together with emails. if email exists in space, it must be marked as userId
	abstract get allRecipients(): Recipient[]; // for table cell showing, incl. slack

	abstract delete(): void;

	billingFeature: BillingFeature | null = null;
	canToggle = false;

	get model() {
		// for updateModelCacheHelper compatibility
		return this.targetObject;
	}

	get id() {
		// need for batch select
		return `${this.targetObject.modelName}-${this.targetObject.id}`;
	}

	get typeBgColor() {
		const color = this.typeColor;
		return color.replace('500)', 'alpha-10)');
	}

	get typeText() {
		return this.mmNotificationsService.getTypeText(this.type);
	}

	get subtitleImage() {
		return '';
	}

	get canEdit() {
		return true;
	}

	get canPreview() {
		return false;
	}

	get isLocked() {
		const feature = this.billingFeature;
		return !!feature && !this.billingService.checkIfCan(feature);
	}

	get isCurrentUserRecipient() {
		return this.emailRecipients.includes(this.currentUserService.numericId!);
	}

	get isValidForList() {
		// frequency requirement not showing e.g. "one off" board shares
		// deliveryTypes requirement for notifications that arent really sent through these channels (valid for e.g. scorecard)
		// mine / recipient requirement for not showing something that i am actually not receiving
		return (
			!!this.frequency.length &&
			!!this.deliveryTypes.length &&
			(this.isMine || this.isCurrentUserRecipient)
		);
	}

	get isRecipientsOnlyMe() {
		const recipients = this.allRecipients;
		return (
			!recipients.length ||
			(recipients.length === 1 &&
				recipients[0]!.email === this.currentUserService.model?.email)
		);
	}

	get frequencyText() {
		const frequency = this.frequency;

		const getFrequencyText = (f: NotificationFilterFrequency[]) => {
			return f
				.map((f) => {
					return this.mmNotificationsService.getFrequencyText(f);
				})
				.join(', ');
		};
		const statusChange = NotificationFilterFrequency.STATUS_CHANGE;
		if (frequency.length > 1 && frequency.includes(statusChange)) {
			// we want to show "On status change" at the end and with "&" (possible w/ goals)
			let text = getFrequencyText(frequency.filter((f) => f !== statusChange));
			text += ` & ${this.mmNotificationsService.getFrequencyText(statusChange)}`;
			return text;
		} else {
			return getFrequencyText(frequency);
		}
	}

	get frequencyTooltipText() {
		let text = `<strong>${this.frequencyTooltipTextTop}</strong>`;

		const createdTooltipText = this.createdTooltipText;
		if (createdTooltipText) {
			text += `<p class="fade">${createdTooltipText}</p>`;
		}

		return text;
	}

	get deliveryTypesIcons() {
		const iconData: { icon: IconName; isStrokeIcon?: boolean; text: string }[] = [];

		const iconNameEmail: IconName = 'Mail',
			iconNamePush: IconName = 'DeviceMobile',
			iconNameSlack: IconName = 'BrandSlack';

		const config = {
			[DeliveryType.EMAIL]: { icon: iconNameEmail, text: 'Email' },
			[DeliveryType.PUSH]: { icon: iconNamePush, text: 'Mobile' },
			[DeliveryType.SLACK]: {
				icon: iconNameSlack,
				isStrokeIcon: true,
				text: 'Slack',
			},
		};

		const deliveryTypes = this.deliveryTypes;
		Object.entries(config).forEach(([deliveryTypeString, iconInfo]) => {
			const deliveryType = +deliveryTypeString;
			if (deliveryTypes.includes(deliveryType)) {
				iconData.push(iconInfo);
			}
		});

		return iconData;
	}

	get createdTooltipText() {
		let text = '';
		const createdBy = this.createdBy,
			createdOn = this.createdOn;
		if (createdBy) {
			text += this.scheduleType === NotificationScheduleType.ALERT ? 'Set' : 'Scheduled';
			text += ` by ${createdBy.name}`;
			if (createdOn) {
				text += `, ${getRelativeTime(createdOn)}`;
			}
		}
		return text;
	}

	get frequencySortProp() {
		// bigger number - more rare
		// this base gets applied to the models that dont employ "schedule" e.g. goals, alerts
		// setting hourOfDay dayOfMonth to higher numbers bcos of sorting vs others
		const frequency = this.frequency;

		const getDeliveryTimeLevelWithDefaults = (
			settings: Partial<Parameters<typeof getDeliveryTimeLevel>[0]>,
		) => {
			return getDeliveryTimeLevel(
				Object.assign(
					{
						hourOfDay: null,
						dayOfWeek: [],
						dayOfMonth: null,
						isWeekly: false,
						isMonthly: false,
						isQuarterly: false,
					},
					settings,
				),
			);
		};
		// getting the lowest frequency value from all available
		let min = Infinity;
		frequency.forEach((f) => {
			let value = Infinity;

			switch (f) {
				case NotificationFilterFrequency.DAILY:
					value = getDeliveryTimeLevelWithDefaults({ hourOfDay: 23 });
					break;
				case NotificationFilterFrequency.WEEKLY:
					value = getDeliveryTimeLevelWithDefaults({ isWeekly: true, dayOfWeek: [1] });
					break;
				case NotificationFilterFrequency.BIWEEKLY:
					value = getDeliveryTimeLevelWithDefaults({
						isWeekly: true,
						dayOfWeek: [1, 2],
					});
					break;
				case NotificationFilterFrequency.MONTHLY:
					value = getDeliveryTimeLevelWithDefaults({ isMonthly: true, dayOfMonth: 30 });
					break;
				case NotificationFilterFrequency.QUARTERLY:
					value = getDeliveryTimeLevelWithDefaults({ isQuarterly: true });
					break;
				// below all "regular" daily/weekly/monthly/quarterly
				case NotificationFilterFrequency.CONDITIONAL:
					value = 1400;
					break;
				case NotificationFilterFrequency.STATUS_CHANGE:
					value = 1401;
					break;
				case NotificationFilterFrequency.ANOMALY:
					value = 1402;
					break;
			}

			if (value < min) {
				min = value;
			}
		});

		// so multi-frequencies are higher
		return min + (frequency.length - 1);
	}

	get scheduleTypeText() {
		switch (this.scheduleType) {
			case NotificationScheduleType.UPDATE:
				return 'Update';
			case NotificationScheduleType.ALERT:
				return 'Alert';
			case NotificationScheduleType.REMINDER:
				return 'Reminder';
		}
	}

	get toggleSortProp() {
		// order: toggled on, toggled off, no toggle at all
		return this.canToggle ? (this.isToggleOn ? 2 : 1) : 0;
	}

	get isToggleOn() {
		// as default for those that have canToggle: false
		return false;
	}

	get canDuplicate() {
		return this.mmNotificationsService.canDuplicateModel(this.targetObject);
	}

	toggle() {
		// keep this empty for those that have canToggle: false
	}

	duplicate() {
		if (this.accountService.handleDemo()) {
			return;
		}

		const newModel = this.mmNotificationsService.createDuplicatedModel(this.targetObject);

		if (!newModel) {
			return;
		}

		this.mmNotificationsService.openEditPopup(newModel, 'notifications');
	}

	preview() {
		// keep this empty for those that have canPreview: false
	}

	handleLimitation() {
		const feature = this.billingFeature;
		if (feature) {
			return this.billingService.checkIfCan(feature, true);
		}
	}
}
